/**
 * ReportViewer — renders weekly report markdown in the same visual style
 * as the HTML email template (stat cards, colored section headers, doc tables).
 *
 * Parses the markdown into structured sections and renders each with
 * matching React components instead of raw markdown.
 */

// ─── Color maps (matching weekly_base.py _html_stat_cards / _html_section) ──

const STAT_COLORS: Record<string, { bg: string; fg: string }> = {
  new:                  { bg: '#e6f4ea', fg: '#2d6a4f' },
  succeeded:            { bg: '#e6f4ea', fg: '#2d6a4f' },
  updated:              { bg: '#e8f0fe', fg: '#1a56db' },
  removed:              { bg: '#fff3e0', fg: '#b45309' },
  skipped:              { bg: '#fff3e0', fg: '#b45309' },
  'skipped (no pdf)':   { bg: '#f3f4f6', fg: '#6b7280' },
  'still pending':      { bg: '#fff3e0', fg: '#b45309' },
  deduplicated:         { bg: '#f3f4f6', fg: '#6b7280' },
  checked:              { bg: '#f3f4f6', fg: '#6b7280' },
  retried:              { bg: '#f3f4f6', fg: '#6b7280' },
  'no pdf':             { bg: '#f3f4f6', fg: '#6b7280' },
  'authorities checked':{ bg: '#f3f4f6', fg: '#6b7280' },
  'documents checked':  { bg: '#f3f4f6', fg: '#6b7280' },
  failed:               { bg: '#fde8e8', fg: '#b91c1c' },
}

const SECTION_COLORS: Record<string, string> = {
  'phase 1':          '#1a56db',
  'processing':       '#1a56db',
  'pending removals': '#6b7280',
  'phase 2':          '#059669',
}

function getSectionColor(title: string): string {
  const lower = title.toLowerCase()
  for (const [key, color] of Object.entries(SECTION_COLORS)) {
    if (lower.includes(key)) return color
  }
  return '#1a56db'
}

function getStatColor(metric: string): { bg: string; fg: string } {
  const lower = metric.toLowerCase()
  return STAT_COLORS[lower] ?? { bg: '#f3f4f6', fg: '#6b7280' }
}

// ─── Markdown parser ────────────────────────────────────────────────────────

interface ReportHeader {
  title: string
  date: string
  environment: string
  duration: string
}

interface StatCard {
  label: string
  value: string
}

interface DocTable {
  title: string
  headers: string[]
  rows: string[][]
}

interface Section {
  title: string
  stats: StatCard[]
  docTables: DocTable[]
}

interface ParsedReport {
  header: ReportHeader
  sections: Section[]
}

function parseReport(markdown: string): ParsedReport {
  const lines = markdown.split('\n')

  const header: ReportHeader = { title: '', date: '', environment: '', duration: '' }
  const sections: Section[] = []
  let currentSection: Section | null = null
  let currentDocTable: DocTable | null = null

  // Track table parsing state
  let tableHeaderRow: string[] | null = null
  let inStatTable = false
  let inDocTable = false

  for (const line of lines) {
    const trimmed = line.trim()

    // H1 — report title
    if (trimmed.startsWith('# ') && !trimmed.startsWith('## ')) {
      header.title = trimmed.slice(2).trim()
      continue
    }

    // Metadata lines
    if (trimmed.startsWith('**Date:**')) { header.date = trimmed.replace('**Date:**', '').trim(); continue }
    if (trimmed.startsWith('**Environment:**')) { header.environment = trimmed.replace('**Environment:**', '').trim(); continue }
    if (trimmed.startsWith('**Duration:**')) { header.duration = trimmed.replace('**Duration:**', '').trim(); continue }

    // H2 — new section
    if (trimmed.startsWith('## ')) {
      currentDocTable = null
      tableHeaderRow = null
      inStatTable = false
      inDocTable = false
      currentSection = { title: trimmed.slice(3).trim(), stats: [], docTables: [] }
      sections.push(currentSection)
      continue
    }

    if (!currentSection) continue

    // H3 — doc table sub-section
    if (trimmed.startsWith('### ')) {
      inStatTable = false
      inDocTable = false
      tableHeaderRow = null
      currentDocTable = { title: trimmed.slice(4).trim(), headers: [], rows: [] }
      currentSection.docTables.push(currentDocTable)
      continue
    }

    // Bullet items → convert to stat cards (e.g. "- Retried: 3")
    if (trimmed.startsWith('- ')) {
      const match = trimmed.slice(2).match(/^(.+?):\s*(\d+)$/)
      if (match) {
        currentSection.stats.push({ label: match[1].trim(), value: match[2] })
      }
      continue
    }

    // Table separator line — marks transition from header to data rows
    if (trimmed.match(/^\|[-| :]+\|$/)) {
      if (tableHeaderRow) {
        if (currentDocTable) {
          currentDocTable.headers = tableHeaderRow
          inDocTable = true
        } else {
          // Stat table (| Metric | Count |) — header row not needed
          inStatTable = true
        }
        tableHeaderRow = null
      }
      continue
    }

    // Table row
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      const cells = trimmed.split('|').slice(1, -1).map(c => c.trim())

      if (inDocTable && currentDocTable) {
        currentDocTable.rows.push(cells)
        continue
      }

      if (inStatTable) {
        if (cells.length >= 2) {
          currentSection.stats.push({ label: cells[0], value: cells[1] })
        }
        continue
      }

      // First table row we see — store as potential header
      tableHeaderRow = cells
      continue
    }

    // Non-table, non-header line — reset table state
    if (trimmed === '') {
      if (!inDocTable && !inStatTable) {
        tableHeaderRow = null
      }
    } else {
      inStatTable = false
      inDocTable = false
      tableHeaderRow = null
      currentDocTable = null
    }
  }

  return { header, sections }
}

// ─── React components (matching weekly_base.py HTML builders exactly) ────────

function StatCards({ stats }: { stats: StatCard[] }) {
  if (stats.length === 0) return null
  return (
    <div style={{ margin: '16px 0' }}>
      {stats.map((stat, i) => {
        const { bg, fg } = getStatColor(stat.label)
        return (
          <div
            key={i}
            style={{
              display: 'inline-block', background: bg, borderRadius: 8,
              padding: '12px 20px', margin: 6, minWidth: 100, textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 28, fontWeight: 700, color: fg }}>{stat.value}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{stat.label}</div>
          </div>
        )
      })}
    </div>
  )
}

function DocTableView({ table }: { table: DocTable }) {
  return (
    <div>
      <h3 style={{ fontSize: 13, fontWeight: 600, color: '#374151', margin: '16px 0 6px' }}>{table.title}</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', margin: '8px 0' }}>
        <thead>
          <tr style={{ background: '#f3f4f6' }}>
            {table.headers.map((h, i) => (
              <th key={i} style={{ padding: '8px 12px', textAlign: 'left', ...(i === 0 ? { width: 30 } : {}) }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((cells, i) => (
            <tr key={i} style={{ background: i % 2 === 1 ? '#f9fafb' : '#ffffff' }}>
              {cells.map((cell, j) => (
                <td
                  key={j}
                  style={{
                    padding: '6px 12px',
                    ...(j === 0 ? { color: '#6b7280', width: 30 } : {}),
                    ...(j === 1 ? { fontFamily: 'monospace', fontSize: 13 } : {}),
                    ...(j >= 2 ? { color: '#6b7280' } : {}),
                  }}
                >
                  {cell.replace(/`/g, '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function SectionBlock({ section }: { section: Section }) {
  const color = getSectionColor(section.title)
  return (
    <div style={{ margin: '24px 0', background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
      <div style={{ background: color, padding: '10px 20px' }}>
        <h2 style={{ margin: 0, color: '#fff', fontSize: 15, fontWeight: 600 }}>{section.title}</h2>
      </div>
      <div style={{ padding: '16px 20px' }}>
        <StatCards stats={section.stats} />
        {section.docTables.map((table, i) => (
          <DocTableView key={i} table={table} />
        ))}
      </div>
    </div>
  )
}

// ─── Main component ─────────────────────────────────────────────────────────

export default function ReportViewer({ markdown }: { markdown: string }) {
  const report = parseReport(markdown)
  const envColor = report.header.environment === 'production' ? '#1a56db' : '#059669'

  return (
    <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      {/* Header — matches _html_wrap */}
      <div style={{ background: '#1e293b', padding: '24px 32px' }}>
        <h1 style={{ margin: 0, color: '#fff', fontSize: 20, fontWeight: 700 }}>
          {report.header.title}
          {report.header.environment && (
            <span style={{
              background: envColor, color: '#fff', padding: '2px 10px',
              borderRadius: 12, fontSize: 12, fontWeight: 600, marginLeft: 10,
            }}>
              {report.header.environment.toUpperCase()}
            </span>
          )}
        </h1>
        <p style={{ margin: '6px 0 0', color: '#94a3b8', fontSize: 13 }}>{report.header.date}</p>
      </div>

      {/* Body — matches _html_wrap inner td */}
      <div style={{ padding: '24px 32px' }}>
        {report.sections.map((section, i) => (
          <SectionBlock key={i} section={section} />
        ))}

        {/* Footer — matches _html_wrap footer */}
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #e5e7eb', color: '#9ca3af', fontSize: 12 }}>
          Completed in {report.header.duration} &middot; Nimonik Chatbot Ingestion Pipeline
        </div>
      </div>
    </div>
  )
}
